<template>
  <a-modal v-model="visible" :title="$t('接口管理')" width="80%">
    <div slot="footer">
      <a-button type="default" :loading="loading" @click="visible = false">{{ $t('取消') }}</a-button>
      <a-button type="primary" :loading="loading" @click="handleCommit">{{ $t('保存') }}</a-button>
    </div>
    <a-form-model ref="form" :model="form" :rules="rules" :labelCol="{ span: 4 }" :wrapperCol="{ span: 18 }">
      <a-card :title="$t('基础信息')">
        <a-row>
          <a-col :span="span12">
            <a-form-model-item :label="$t('接口名称')" prop="name">
              <a-input v-model="form.name" />
            </a-form-model-item>
          </a-col>
          <a-col :span="span12">
            <a-form-model-item :label="$t('模块')" prop="module">
              <a-input v-model="form.module" />
            </a-form-model-item>
          </a-col>
          <a-col :span="span12">
            <a-form-model-item :label="$t('功能')" prop="actionType">
              <a-select v-model="form.actionType" :options="actionTypeOptions" @change="handleActionTypeChange" />
            </a-form-model-item>
          </a-col>
          <a-col :span="span12">
            <a-form-model-item :label="$t('HTTP方法')" prop="httpMethod">
              <a-select v-model="form.httpMethod" :options="[{ label: 'get', value: 'get' }, { label: 'post', value: 'post' }]" />
            </a-form-model-item>
          </a-col>
          <a-col :span="span12">
            <a-form-model-item :label="$t('权限字符')" prop="permission">
              <a-input v-model="form.permission" />
            </a-form-model-item>
          </a-col>
          <a-col :span="span12" v-if="form.actionType === 'select'">
            <a-form-model-item :label="$t('数据范围')" prop="dataScope">
              <a-select v-model="form.dataScope" :options="dataScopeOptions" />
            </a-form-model-item>
          </a-col>
        </a-row>
      </a-card>

      <a-card :title="$t('数据库')" class="mt10">
        <a-row :gutter="[10, 10]">
          <a-col :span="span12">
            <a-form-model-item :label="$t('数据库')" prop="dbId">
              <a-select v-model="form.dbId" @change="handleChangeDB" :options="dbOptions" :dropdownMatchSelectWidth="false" />
            </a-form-model-item>
          </a-col>
        </a-row>
        <a-descriptions bordered>
          <a-descriptions-item :label="$t('类型')">{{ dbInfo.type }}</a-descriptions-item>
          <a-descriptions-item :label="$t('主机')">{{ dbInfo.host }}</a-descriptions-item>
          <a-descriptions-item :label="$t('端口')">{{ dbInfo.port }}</a-descriptions-item>
          <a-descriptions-item :label="$t('服务名')" v-if="dbInfo.type === 'oracle'">{{ dbInfo.serviceName }}</a-descriptions-item>
          <a-descriptions-item :label="$t('数据库')">{{ dbInfo.db }}</a-descriptions-item>
        </a-descriptions>
      </a-card>

      <a-card :title="$t('相关表信息')" class="mt10">
        <a-button slot="extra" @click="handleAddTable">{{ $t('添加表') }}</a-button>
        <a-table :columns="tableInfoColumns" :dataSource="selectedTables" :pagination="false">
          <div slot="action" slot-scope="text, record, index">
            <a @click="selectedTables.splice(index, 1)">{{ $t('删除') }}</a>
          </div>
          <div slot="relationType" slot-scope="text, record">
            <a-select v-model="record.relationType" :options="relationTypeOptions" :dropdownMatchSelectWidth="false" @change="selectedTables = [...selectedTables]" />
          </div>
          <div slot="paramName" slot-scope="text, record">
            <a-input v-model="record.paramName" />
          </div>
          <div slot="paramTitle">
            <a-tooltip>
              <div slot="title">
                {{ $t('参数名可不设置，若进行设置，则设置参数名后，对应表的查询条件应该在键为该参数名的json对象中；该表的返回数据也会在键为该参数名的json对象中。') }}
              </div>
              <span>{{ $t('参数名') }}<a-icon style="margin-left: 5px; font-size: 12px;" type="question-circle" /></span>
            </a-tooltip>
          </div>
          <div slot="alias" slot-scope="text, record">
            <a-input v-model="record.alias" />
          </div>
          <div slot="aliasTitle">
            <a-tooltip>
              <div slot="title">
                {{ $t('当同一个表添加多次时，可对这些表设置别名以进行区分') }}
              </div>
              <span>{{ $t('表别名') }}<a-icon style="margin-left: 5px; font-size: 12px;" type="question-circle" /></span>
            </a-tooltip>
          </div>
        </a-table>
      </a-card>

      <a-card :title="$t('表关联关系')" class="mt10" v-if="selectedTables && selectedTables.length > 1">
        <a-button slot="extra" @click="handleAddRelation">{{ $t('添加关联字段') }}</a-button>
        <a-table :columns="relationColumns" :dataSource="relationList" :pagination="false">
          <div slot="action" slot-scope="text, record, index">
            <a @click="relationList.splice(index, 1)">{{ $t('删除') }}</a>
          </div>
          <div slot="tableName" slot-scope="text, record">
            <a-select v-model="record.tableName" :options="selectedTables.map(item => ({ label: item.alias?.length ? `${item.tableName}(${item.alias})` : item.tableName, value: item.alias?.length ? `${item.tableName}${separator}${item.alias}` : item.tableName }))" :dropdownMatchSelectWidth="false" @change="record.columnName = null" />
          </div>
          <div slot="columnName" slot-scope="text, record">
            <div class="column-selection" @click="handleAddRelatedColumn(record, 1)">
              <span>{{ text }}</span>
              <a-icon type="right" class="arrow" />
            </div>
          </div>
          <div slot="relateTableName" slot-scope="text, record">
            <a-select v-model="record.relateTableName" :options="selectedTables.map(item => ({ label: item.alias?.length ? `${item.tableName}(${item.alias})` : item.tableName, value: item.alias?.length ? `${item.tableName}${separator}${item.alias}` : item.tableName }))" :dropdownMatchSelectWidth="false" @change="record.relateColumnName = null" />
          </div>
          <div slot="relateColumnName" slot-scope="text, record">
            <div class="column-selection" @click="handleAddRelatedColumn(record, 2)">
              <span>{{ text }}</span>
              <a-icon type="right" class="arrow" />
            </div>
          </div>
          <div slot="relateType" slot-scope="text, record">
            <a-select v-model="record.relateType" :options="[{ label: $t('相等'), value: '1' }]" />
          </div>
        </a-table>
      </a-card>

      <a-card :title="$t('查询参数信息')" class="mt10" v-if="form.actionType != 'insert'">
        <a-button slot="extra" @click="handleAddParams">{{ $t('添加参数') }}</a-button>
        <a-table :columns="paramsColumns" :dataSource="paramsList" :pagination="false">
          <div slot="action" slot-scope="text, record, index">
            <a @click="paramsList.splice(index, 1)">{{ $t('删除') }}</a>
          </div>
          <div slot="name" slot-scope="text, record">
            <a-input v-model="record.name" :style="{ border: record.name && record.name.trim().length > 0 ? '' : '1px solid red' }" />
          </div>
          <div slot="tableName" slot-scope="text, record">
            {{ record.tableAlias?.length ? `${text}(${record.tableAlias})` : text }}
          </div>
          <div slot="filedName" slot-scope="text, record">
            {{ text }}
          </div>
          <div slot="description" slot-scope="text, record">
            <a-input v-model="record.description" />
          </div>
          <div slot="condition" slot-scope="text, record">
            <a-select v-model="record.condition" :options="['=', '!=', '>', '<', '>=', '<=', 'in', 'like'].map(item => ({ label: item, value: item }))" />
          </div>
        </a-table>
      </a-card>

      <a-card :title="$t('排序信息')" class="mt10" v-if="form.actionType === 'select'">
        <a-button slot="extra" @click="handleAddSortFields">{{ $t('添加参数') }}</a-button>
        <a-table :columns="sortColumns" :dataSource="sortList" :pagination="false">
          <div slot="action" slot-scope="text, record, index">
            <a @click="sortList.splice(index, 1)">{{ $t('删除') }}</a>
          </div>
          <div slot="tableName" slot-scope="text, record">
            {{ record.tableAlias?.length ? `${text}(${record.tableAlias})` : text }}
          </div>
          <div slot="fieldName" slot-scope="text, record">
            {{ text }}
          </div>
          <div slot="type" slot-scope="text, record">
            <a-select v-model="record.type" :options="[{label: $t('升序'), value: 'ASC'}, {label: $t('降序'), value: 'DESC'}]" />
          </div>
        </a-table>
      </a-card>

      <a-card v-if="['select', 'update', 'insert'].indexOf(form.actionType) >= 0" :title="form.actionType == 'select' ? $t('返回字段信息') : form.actionType == 'update' ? $t('更新字段信息') : form.actionType == 'insert' ? $t('插入字段信息') : ''" class="mt10">
        <a-button slot="extra" @click="handleAddResponse">{{ $t('添加参数') }}</a-button>
        <a-table :columns="responseColumns" :dataSource="responseList" :pagination="false">
          <div slot="action" slot-scope="text, record, index">
            <a @click="responseList.splice(index, 1)">{{ $t('删除') }}</a>
          </div>
          <div slot="name" slot-scope="text, record">
            <a-input v-model="record.name" :style="{ border: record.name && record.name.trim().length > 0 ? '' : '1px solid red' }"  />
          </div>
          <div slot="tableName" slot-scope="text, record">
            {{ record.tableAlias?.length ? `${text}(${record.tableAlias})` : text }}
          </div>
          <div slot="filedName" slot-scope="text, record">
            {{ text }}
          </div>
          <div slot="description" slot-scope="text, record">
            <a-input v-model="record.description" />
          </div>
          <div slot="dataType" slot-scope="text, record">
            {{ record.jdbcType }}
          </div>
        </a-table>
      </a-card>
    </a-form-model>
    <AppDbTableSelectModal ref="tableList" @ok="handleTableAdded" />
    <AppDbTableColumnSelectModal ref="columnList" @ok="handleColumnAdded" />
    <AppDbTableColumnSelectModal ref="responseList" @ok="handleResponseAdded" />
    <AppDbTableColumnSelectModal ref="relateColumnList" selectType="radio" @ok="handleRelatedColumnAdded" />
    <AppDbTableColumnSelectModal ref="sortList" @ok="handleSortAdded" />
  </a-modal>
</template>
<script>
import { getApiDetail, saveApi } from '@/api/app/apimanage'
import AppDbTableColumnSelectModal from './AppDbTableColumnSelectModal.vue'
import AppDbTableSelectModal from './AppDbTableSelectModal.vue'
import { getDbList } from '@/api/app/db'

const separator = "$$$!!!"

export default {
  components: {
    AppDbTableColumnSelectModal,
    AppDbTableSelectModal,
  },
  data() {
    return {
      separator: separator,
      visible: false,
      app: {},
      api: {},
      form: {},
      loading: false,
      span12: 12,
      actionTypeOptions: ['select', 'update', 'insert', 'delete'].map(item => ({ label: item, value: item })),
      dataScopeOptions: [{ label: this.$t('默认'), value: '0' }, { label: this.$t('本部门'), value: '1' }, { label: this.$t('本人'), value: '2' }],
      dbInfo: {},
      dbOptions: [],
      selectedTables: [],
      paramsList: [],
      responseList: [],
      relationList: [],
      sortList: [],
    }
  },
  computed: {
    rules() {
      return {
        name: [{ required: true, message: this.$t('请输入') }],
        module: [{ required: true, message: this.$t('请输入') }],
        httpMethod: [{ required: true, message: this.$t('请选择') }],
        actionType: [{ required: true, message: this.$t('请选择') }],
        dbId: [{ required: true, message: this.$t('请选择') }],
      }
    },
    tableInfoColumns() {
      const columns = [
        { title: this.$t('表名'), dataIndex: 'tableName' },
        { title: this.$t('备注'), dataIndex: 'tableComment' },
        { title: this.$t('与主表关系'), dataIndex: 'relationType', scopedSlots: { customRender: 'relationType' } },
        { dataIndex: 'paramName', scopedSlots: { customRender: 'paramName', title: 'paramTitle' } },
        { dataIndex: 'alias', scopedSlots: { customRender: 'alias', title: 'aliasTitle' } },
        { title: this.$t('操作'), dataIndex: 'action', scopedSlots: { customRender: 'action' } },
      ]
      if (this.form.actionType === 'select') {
        return columns
      }
      if (this.form.actionType === 'delete') {
        return [columns[0], columns[1], columns[2], columns[5]]
      }
      return [columns[0], columns[1], columns[2], columns[3], columns[5]]
    },
    relationTypeOptions() {
      if (this.form.actionType != 'select') {
        return [
          { label: this.$t('主表'), value: '0' },
          { label: this.$t('从表'), value: '5' },
        ]
      }
      return [
        { label: this.$t('主表'), value: '0' },
        { label: 'left join', value: '1' },
        { label: 'right join', value: '2' },
        { label: 'inner join', value: '3' },
        { label: this.$t('从表'), value: '5' },
      ]
    },
    paramsColumns() {
      return [
        { title: this.$t('字段名'), dataIndex: 'name', scopedSlots: { customRender: 'name' } },
        { title: this.$t('对应表'), dataIndex: 'tableName', scopedSlots: { customRender: 'tableName' } },
        { title: this.$t('对应表字段'), dataIndex: 'filedName', scopedSlots: { customRender: 'filedName' } },
        { title: this.$t('字段描述'), dataIndex: 'description', scopedSlots: { customRender: 'description' } },
        { title: this.$t('条件'), dataIndex: 'condition', scopedSlots: { customRender: 'condition' } },
        { title: this.$t('操作'), dataIndex: 'action', scopedSlots: { customRender: 'action' } },
      ]
    },
    sortColumns() {
      return [
        { title: this.$t('表'), dataIndex: 'tableName', scopedSlots: { customRender: 'tableName' } },
        { title: this.$t('字段'), dataIndex: 'fieldName', scopedSlots: { customRender: 'fieldName' } },
        { title: this.$t('类型'), dataIndex: 'type', scopedSlots: { customRender: 'type' } },
        { title: this.$t('操作'), dataIndex: 'action', scopedSlots: { customRender: 'action' } },
      ]
    },
    responseColumns() {
      return [
        { title: this.$t('字段名'), dataIndex: 'name', scopedSlots: { customRender: 'name' } },
        { title: this.$t('对应表'), dataIndex: 'tableName', scopedSlots: { customRender: 'tableName' } },
        { title: this.$t('对应表字段'), dataIndex: 'filedName', scopedSlots: { customRender: 'filedName' } },
        { title: this.$t('字段描述'), dataIndex: 'description', scopedSlots: { customRender: 'description' } },
        { title: this.$t('字段类型'), dataIndex: 'dataType', scopedSlots: { customRender: 'dataType' } },
        { title: this.$t('操作'), dataIndex: 'action', scopedSlots: { customRender: 'action' } },
      ]
    },
    relationColumns() {
      return [
        { title: this.$t('表名'), dataIndex: 'tableName', scopedSlots: { customRender: 'tableName' } },
        { title: this.$t('字段'), dataIndex: 'columnName', scopedSlots: { customRender: 'columnName' } },
        { title: this.$t('被关联表名'), dataIndex: 'relateTableName', scopedSlots: { customRender: 'relateTableName' } },
        { title: this.$t('被关联字段'), dataIndex: 'relateColumnName', scopedSlots: { customRender: 'relateColumnName' } },
        { title: this.$t('关联方式'), dataIndex: 'relateType', scopedSlots: { customRender: 'relateType' } },
        { title: this.$t('操作'), dataIndex: 'action', scopedSlots: { customRender: 'action' } },
      ]
    }
  },
  methods: {
    show(app, api) {
      this.app = { ...app }
      this.api = { ...api }
      this.dbInfo = {}
      this.dbOptions = []
      this.selectedTables = []
      this.paramsList = []
      this.sortList = []
      this.responseList = []
      this.relationList = []
      this.resetForm()
      this.loadData()
      this.visible = true
    },
    resetForm() {
      this.form = {
        ...this.api
      }
    },
    async loadData() {
      await this.loadDBData()
      await this.loadDetail()
      this.resetForm()
    },
    async loadDBData() {
      const res = await getDbList(this.app.id)
      this.dbOptions = res.data?.map(item => {
        return {
          ...item,
          label: `${item.type}:${item.db}@${item.host}:${item.port}`,
          value: item.id
        }
      }) ?? []
      this.dbInfo = this.dbOptions.length > 0 ? (this.dbOptions.find(item => item.value === this.api.dbId) || {}) : {}
    },
    async loadDetail() {
      if (!this.api.id) {
        return
      }
      const res = await getApiDetail(this.api.id)
      this.api = {
        ...this.api,
        ...res.data
      }
      this.selectedTables = res.data?.tableList ?? []
      this.paramsList = res.data?.queryFieldInfoList ?? []
      this.sortList = res.data?.sortInfoList ?? []
      this.responseList = res.data?.responseFieldInfoList ?? []
      this.relationList = res.data?.relateFieldList ?? []
      this.relationList.forEach(r => {
        if (r.tableAlias?.length) {
          r.tableName = `${r.tableName}${separator}${r.tableAlias}`
        }
        if (r.relateTableAlias?.length) {
          r.relateTableName = `${r.relateTableName}${separator}${r.relateTableAlias}`
        }
      })
    },
    handleActionTypeChange(v) {
      if (v === 'select') {
        this.form.httpMethod = this.form.httpMethod || 'get'
        return
      }
      this.selectedTables && this.selectedTables.forEach(table => {
        table.paramName = v=== 'delete' ? '' : table.paramName 
        table.alias = ''
        table.relationType = table.relationType === '0' ? table.relationType : this.relationTypeOptions[this.relationTypeOptions.length - 1].value
      })
      this.paramsList && this.paramsList.forEach(p => {
        p.tableAlias = ''
      })
      this.sortList && this.sortList.forEach(p => {
        p.tableAlias = ''
      })
      this.responseList && this.responseList.forEach(p => {
        p.tableAlias = ''
      })
      this.form.httpMethod = 'post'
    },
    handleChangeDB(v) {
      this.dbInfo = this.dbOptions.length > 0 ? (this.dbOptions.find(item => item.value === v) || {}) : {}
      this.selectedTables = []
      this.paramsList = []
      this.sortList = []
      this.responseList = []
      this.relationList = []
    },
    handleAddTable() {
      if (!this.dbInfo.db) {
        this.$message.error(this.$t('请选择数据库'))
        return
      }
      this.$refs.tableList.show(this.app.id, this.dbInfo.db)
    },
    handleTableAdded(list) {
      if (!(list?.length)) {
        return
      }
      const allTables = this.selectedTables.map(item => item.tableName)
      const arr = this.form.actionType === 'select' ? list : list.filter(item => allTables.indexOf(item.tableName) < 0)
      arr.forEach(item => item.relationType = '0')
      this.selectedTables = [
        ...this.selectedTables,
        ...arr
      ]
    },
    handleAddParams() {
      if (this.selectedTables.length == 0) {
        this.$message.error(this.$t('请选择数据表'))
        return
      }
      this.$refs.columnList.show(this.app.id, this.dbInfo.db, this.selectedTables.map(item => ({ tableName: item.tableName, alias: item.alias, label: item.alias?.length ? `${item.tableName}(${item.alias})` : item.tableName, value: item.alias?.length ? `${item.tableName}${separator}${item.alias}` : item.tableName })))
    },
    handleColumnAdded(list) {
      const index = this.getTempIndex(this.paramsList)
      const selectedParamsKeys = this.paramsList.map(item => {
        return `${item.tableName}-${item.filedName}-${item.tableAlias}`
      })
      const arr = list.filter(item => selectedParamsKeys.indexOf(`${item.tableName}-${item.columnName}-${item.tableAlias}`) < 0).map((item, idx) => {
        return {
          id: `TEMP-ID-${index + idx + 1}`,
          tableName: item.tableName,
          filedName: item.columnName,
          tableAlias: item.tableAlias,
          name: item.columnName,
          description: item.comments,
          jdbcType: item.jdbcType || item.dataType,
          condition: '='
        }
      })
      this.paramsList = [
        ...this.paramsList,
        ...arr
      ]
    },
    handleAddSortFields() {
      if (this.selectedTables.length == 0) {
        this.$message.error(this.$t('请选择数据表'))
        return
      }
      this.$refs.sortList.show(this.app.id, this.dbInfo.db, this.selectedTables.map(item => ({ tableName: item.tableName, alias: item.alias, label: item.alias?.length ? `${item.tableName}(${item.alias})` : item.tableName, value: item.alias?.length ? `${item.tableName}${separator}${item.alias}` : item.tableName })))
    },
    handleSortAdded(list) {
      const index = this.getTempIndex(this.sortList)
      const selectedParamsKeys = this.sortList.map(item => {
        return `${item.tableName}-${item.fieldName}-${item.tableAlias}`
      })
      const arr = list.filter(item => selectedParamsKeys.indexOf(`${item.tableName}-${item.columnName}-${item.tableAlias}`) < 0).map((item, idx) => {
        return {
          id: `TEMP-ID-${index + idx + 1}`,
          tableName: item.tableName,
          fieldName: item.columnName,
          tableAlias: item.tableAlias,
          type: 'ASC'
        }
      })
      this.sortList = [
        ...this.sortList,
        ...arr
      ]
    },
    handleAddResponse() {
      if (this.selectedTables.length == 0) {
        this.$message.error(this.$t('请选择数据表'))
        return
      }
      this.$refs.responseList.show(this.app.id, this.dbInfo.db, this.selectedTables.map(item => ({ tableName: item.tableName, alias: item.alias, label: item.alias?.length ? `${item.tableName}(${item.alias})` : item.tableName, value: item.alias?.length ? `${item.tableName}${separator}${item.alias}` : item.tableName })))
    },
    handleResponseAdded(list) {
      const index = this.getTempIndex(this.responseList)
      const selectedParamsKeys = this.responseList.map(item => {
        return `${item.tableName}-${item.filedName}-${item.tableAlias}`
      })
      console.log(selectedParamsKeys)
      const arr = list.filter(item => selectedParamsKeys.indexOf(`${item.tableName}-${item.columnName}-${item.tableAlias}`) < 0).map((item, idx) => {
        return {
          id: `TEMP-ID-${index + idx + 1}`,
          tableName: item.tableName,
          filedName: item.columnName,
          tableAlias: item.tableAlias,
          name: item.columnName,
          description: item.comments,
          jdbcType: item.jdbcType || item.dataType
        }
      })
      this.responseList = [
        ...this.responseList,
        ...arr
      ]
    },
    handleAddRelation() {
      const index = this.getTempIndex(this.relationList)
      this.relationList = [
        ...this.relationList,
        {
          id: `TEMP-ID-${index + 1}`,
          relateType: '1'
        }
      ]
    },
    handleAddRelatedColumn(record, type) {
      if ((type === 1 && !record.tableName) || (type === 2 && !record.relateTableName)) {
        return
      }
      let tableName = type === 1 ? record.tableName : record.relateTableName
      let alias = null
      if (tableName && tableName.indexOf(separator) >= 0) {
        const components = tableName.split(separator)
        tableName = components[0]
        alias = components[1]
      }
      this.$refs.relateColumnList.show(this.app.id, this.dbInfo.db, [{ tableName, alias, label: alias?.length ? `${tableName}(${alias})` : tableName, value: alias?.length ? `${tableName}${separator}${alias}` : tableName }], { id: record.id, type })
    },
    handleRelatedColumnAdded(list, userData) {
      if (!list || list.length == 0 || !userData) {
        return
      }
      const column = list[0]
      const { id, type } = userData
      const data = this.relationList.find(item => item.id === id)
      if (!data) {
        return
      }
      if (type === 1) {
        data.columnName = column.columnName
      } else if (type === 2) {
        data.relateColumnName = column.columnName
      }
      this.relationList = [...this.relationList]
    },
    getTempIndex(list) {
      if (!list || list.length == 0) {
        return 0
      }
      const indexList = list.map(item => item.id).filter(item => {
        const reg = /TEMP-ID-[d]+/g
        return reg.test(item)
      }).map(item => item.replace('TEMP-ID-', '')).sort((a, b) => {
        return parseInt(a) - parseInt(b)
      })
      const index = indexList && indexList.length > 0 ? '0' : indexList[indexList.length - 1]
      return parseInt(index)
    },
    handleCommit() {
      const baseInfo = { ...this.form }
      const queryFieldInfoList = [ ...this.paramsList.map(item => ({ ...item, name: item.name.trim() })) ]
      const sortInfoList = [ ...this.sortList.map(item => ({ ...item })) ]
      const responseFieldInfoList = [ ...this.responseList.map(item => ({ ...item, name: item.name.trim() })) ]
      const tableList = [ ...this.selectedTables ]
      const relateFieldList = [ ...this.relationList ]
      const commitData = {
        id: baseInfo.id,
        name: baseInfo.name.trim(),
        module: baseInfo.module.trim(),
        httpMethod: baseInfo.httpMethod,
        apiUrl: baseInfo.apiUrl,
        actionType: baseInfo.actionType,
        permission: baseInfo.permission,
        appId: baseInfo.appId || this.app.id,
        dbId: baseInfo.dbId,
        dataScope: baseInfo.dataScope,
        queryFieldInfoList,
        responseFieldInfoList,
        tableList,
        relateFieldList,
        sortInfoList
      }
      if (tableList.length == 0) {
        this.$message.error(this.$t('请添加相关表信息'))
        return
      }
      if (relateFieldList.length > 0 && relateFieldList.filter(item => !(item.columnName?.length) || !(item.relateColumnName?.length)).length > 0) {
        this.$message.error(this.$t('请设置关联字段'))
        return
      }
      if (queryFieldInfoList && queryFieldInfoList.filter(item => item.name.length == 0).length > 0) {
        this.$message.error(this.$t('字段名不可为空'))
        return
      }
      if (responseFieldInfoList && responseFieldInfoList.filter(item => item.name.length == 0).length > 0) {
        this.$message.error(this.$t('字段名不可为空'))
        return
      }
      relateFieldList.forEach(r => {
        if (r.tableName && r.tableName.indexOf(separator) >= 0) {
          const components = r.tableName.split(separator)
          r.tableName = components[0]
          r.tableAlias = components[1]
        }
        if (r.relateTableName && r.relateTableName.indexOf(separator) >= 0) {
          const components = r.relateTableName.split(separator)
          r.relateTableName = components[0]
          r.relateTableAlias = components[1]
        }
      })
      this.loading = true
      saveApi(commitData).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('保存成功'))
          this.$emit('ok', commitData)
          this.visible = false
        } else {
          this.$message.error(res.message || this.$t('保存失败'))
        }
      }).catch(err => {
        this.$message.error(this.$t('保存失败'))
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>
<style lang="less" scoped>
.mt10 {
  margin-top: 20px;
}

.column-selection {
  height: 32px;
  border: 1px solid #ddd;
  min-width: 100px;
  border-radius: 4px;
  position: relative;
  background-color: #fff;
  cursor: pointer;
  overflow: hidden;

  span {
    line-height: 32px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 14px;
    color: #666;
    margin: 0 30px 0 15px;
  }

  &:hover {
    border: 1px solid #1890ff;
  }

  .arrow {
    position: absolute;
    right: 15px;
    font-size: 12px;
    color: #bbb;
    line-height: 32px;
  }
}
</style>